/**
 * pages/ImportHub.jsx — Phase 84.2 + 84.2.1
 *
 * Adaptive single-page landing for /import. One drop-zone auto-detects
 * the file format + content class, then MOUNTS the right downstream
 * flow inline (no page navigation). This keeps the user on `/import`
 * and preserves the File object across the handoff — fixing the
 * silent-failure regression where uploads to /import/statements never
 * received the file.
 *
 *   statements  → renders <StatementProcessor prefillFile={file}/> inline
 *   investments → opens <ImportInvestmentsDialog/> as a modal
 *   accounts    → coming soon (Phase 84.3+)
 *   recurring   → coming soon (Phase 84.3+)
 *
 * The user can override the detected class before proceeding. The
 * confidence pill + reasons list keep the decision honest and legible.
 */
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import usePageTitle from '@/hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  UploadCloud, FileText, FileSpreadsheet, FileType, Sparkles,
  ArrowRight, AlertTriangle, Loader2, Check, RefreshCw,
} from 'lucide-react';
import StatementProcessor from '@/components/import/statements/StatementProcessor';
import ImportInvestmentsDialog from '@/components/ImportInvestmentsDialog';
import UploadedStatementsPanel from '@/components/import/UploadedStatementsPanel';
import UnlinkedTransactionsCard from '@/components/import/UnlinkedTransactionsCard';
import { useImportBlock } from '@/components/onboarding/ImportBlockedDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMAT_ICONS = { pdf: FileText, xlsx: FileSpreadsheet, csv: FileType, tsv: FileType, ofx: FileType, qfx: FileType };
const FORMAT_LABELS = {
  csv: 'CSV', tsv: 'TSV', pdf: 'PDF', ofx: 'OFX', qfx: 'QFX', xlsx: 'XLSX', unknown: 'Unknown format',
};
const CLASS_LABELS = {
  statements: 'Bank / card statements',
  investments: 'Investments / holdings',
  accounts: 'Accounts (bulk create)',
  recurring: 'Recurring commitments',
  unknown: 'Not detected',
};

// Which classes have an inline processor wired up. Accounts + recurring
// bulk-create flows land in a follow-up slice; for now we tell the user.
const SUPPORTED_CLASSES = new Set(['statements', 'investments']);

async function authedFormPost(path, formData) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const res = await axios.post(`${API}${path}`, formData, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
}

export default function ImportHub() {
  usePageTitle('Import');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detection, setDetection] = useState(null);   // response from /api/import/detect
  const [override, setOverride] = useState(null);     // user-chosen content_class
  const [dragOver, setDragOver] = useState(false);
  // Phase 84.2.1 — when non-null, we mount the corresponding processor
  // inline on this page instead of navigating away.
  const [activeProcessor, setActiveProcessor] = useState(null); // 'statements' | 'investments' | null

  // Phase 84.22 — block real-statement imports while demo data is present.
  // `checkBeforeImport()` opens the "Clear demo first" dialog and returns
  // true if the caller must abort.
  const { checkBeforeImport, dialog: importBlockDialog } = useImportBlock();

  // Phase 84.2.2 — Deep-link support. The NotificationBell links to
  // `/import?job=<jobId>` when a parse job finishes ("Review and Import").
  // Before Phase 84.2, `/import` rendered CSVImport directly and consumed
  // `?job=`. Now `/import` renders this hub — so on any load that carries
  // the deep-link param we must immediately mount the statement processor
  // inline. The underlying CSVImport already handles `?job=` via its own
  // useSearchParams hook, so once it's mounted the preview loads itself.
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (jobId && activeProcessor !== 'statements') {
      setActiveProcessor('statements');
    }
  }, [searchParams, activeProcessor]);

  const runDetection = async (uploadedFile) => {
    if (!uploadedFile) return;
    setFile(uploadedFile);
    setDetection(null);
    setOverride(null);
    setDetecting(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadedFile);
      const res = await authedFormPost('/api/import/detect', fd);
      setDetection(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not analyse the file.';
      toast.error(msg);
      setFile(null);
    } finally {
      setDetecting(false);
    }
  };

  const chosenClass = override || detection?.content_class || 'unknown';
  const isSupported = SUPPORTED_CLASSES.has(chosenClass);

  const reset = () => {
    setFile(null);
    setDetection(null);
    setOverride(null);
    setActiveProcessor(null);
    if (fileRef.current) fileRef.current.value = '';
    // Phase 84.2.2 — Clear deep-link `?job=` if present so the effect
    // above does not immediately remount the statement processor.
    if (searchParams.get('job')) {
      navigate('/import', { replace: true });
    }
  };

  const proceed = () => {
    if (!isSupported) return;
    // Phase 84.2.1 — mount the processor inline instead of navigating.
    // This preserves the File object across the handoff and lets the
    // downstream flow (e.g. PDF password modal) fire correctly.
    setActiveProcessor(chosenClass);
  };

  // ---------------------------------------------------------------------
  // Rendering — inline processors take over the whole hub when active.
  // ---------------------------------------------------------------------

  // Statements: mount StatementProcessor inline with the pre-dropped file.
  if (activeProcessor === 'statements') {
    return (
      <div className="max-w-5xl mx-auto" data-testid="import-hub-processor-statements">
        <StatementProcessor prefillFile={file} onBack={reset} />
      </div>
    );
  }

  // Investments: open the existing dialog inline. Closing returns to the picker.
  if (activeProcessor === 'investments') {
    return (
      <div className="max-w-3xl mx-auto" data-testid="import-hub-processor-investments">
        {/*
          Phase 84.2.1 — We render nothing behind the modal on purpose:
          the ImportInvestmentsDialog covers the viewport, and any card
          rendered here would be blocked by the modal overlay anyway.
          Closing the dialog (X or ESC) fires onOpenChange(false), which
          calls reset() and returns the user to the picker.
        */}
        <ImportInvestmentsDialog
          open={true}
          onOpenChange={(next) => { if (!next) reset(); }}
          onCommitted={() => {
            toast.success('Investments imported.');
            reset();
            navigate('/accounts?tab=investments');
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="import-hub">
      {/* Phase 84.22 — mounted so `checkBeforeImport()` can open it. */}
      {importBlockDialog}
      <header>
        <h1 className="font-display text-2xl md:text-3xl font-medium text-charcoal" style={{ letterSpacing: '-0.015em' }}>
          Import
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Drop any file — a bank statement, an investments export, an accounts list — and we&rsquo;ll figure out what it is.
        </p>
      </header>

      {/* Drop zone (shown when no file yet) */}
      {!file && (
        <label
          className={`block rounded-2xl border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border/70 bg-card hover:bg-accent/30'
          }`}
          data-testid="import-hub-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer?.files?.[0];
            if (!f) return;
            // Phase 84.22 — block if demo data present.
            if (checkBeforeImport()) return;
            runDetection(f);
          }}
        >
          <UploadCloud className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="mt-3 font-display text-lg font-semibold">Drop your file here</div>
          <div className="text-sm text-muted-foreground mt-1">PDF · CSV · OFX · XLSX — or click to browse</div>
          <div className="text-xs text-muted-foreground mt-3 max-w-md mx-auto opacity-80">
            We&rsquo;ll auto-detect what kind of import it is (statements, investments, accounts, recurring)
            and route you to the right next step. No data is saved until you confirm.
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".csv,.tsv,.pdf,.ofx,.qfx,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (checkBeforeImport()) { e.target.value = ''; return; }
              runDetection(f);
            }}
            data-testid="import-hub-file-input"
          />
        </label>
      )}

      {/* Detecting spinner */}
      {file && detecting && (
        <Card className="rounded-2xl" data-testid="import-hub-detecting">
          <CardContent className="py-8 flex items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Looking at your file&hellip;
          </CardContent>
        </Card>
      )}

      {/* Detection result card */}
      {detection && !detecting && (
        <Card className="rounded-2xl" data-testid="import-hub-detection-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              This looks like&hellip;
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Top summary row */}
            <div className="flex items-start gap-4">
              {(() => {
                const Icon = FORMAT_ICONS[detection.format] || FileType;
                return (
                  <div className="h-12 w-12 rounded-xl bg-muted grid place-items-center shrink-0" data-testid="import-hub-format-icon">
                    <Icon className="h-6 w-6 text-muted-foreground" />
                  </div>
                );
              })()}
              <div className="min-w-0 flex-1">
                <div className="text-lg font-semibold" data-testid="import-hub-detected-class">
                  {CLASS_LABELS[detection.content_class] || CLASS_LABELS.unknown}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                  <span>{FORMAT_LABELS[detection.format] || detection.format}</span>
                  <span>&middot;</span>
                  <span className="truncate max-w-[16rem]">{detection.filename}</span>
                  <span>&middot;</span>
                  <span>{Math.max(1, Math.round((detection.size_bytes || 0) / 1024))} KB</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 max-w-[140px]">
                    <Progress value={Math.round((detection.confidence || 0) * 100)} className="h-1.5" data-testid="import-hub-confidence-bar" />
                  </div>
                  <Badge variant="outline" className="rounded-full text-[10px]" data-testid="import-hub-confidence-badge">
                    {Math.round((detection.confidence || 0) * 100)}% confidence
                  </Badge>
                </div>
              </div>
            </div>

            {/* Warnings, if any */}
            {(detection.warnings || []).length > 0 && (
              <Alert className="rounded-xl border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/5" data-testid="import-hub-warnings">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <ul className="list-disc pl-4 space-y-0.5 text-xs">
                    {detection.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Recognised columns (text-based only) */}
            {(detection.recognised_headers || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground/70 font-medium mb-1.5">
                  Recognised columns
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {detection.recognised_headers.map((h, i) => (
                    <Badge key={i} variant="secondary" className="rounded-full text-[10px] font-normal">
                      {h}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Preview rows */}
            {(detection.preview_rows || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground/70 font-medium mb-1.5">
                  Sample rows
                </div>
                <div className="rounded-xl border border-border/60 overflow-x-auto max-w-full">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40">
                      <tr>
                        {(detection.recognised_headers || []).slice(0, 6).map((h, i) => (
                          <th key={i} className="px-2 py-1.5 text-left font-medium">{h || `col ${i + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detection.preview_rows.slice(0, 5).map((r, i) => (
                        <tr key={i} className="border-t border-border/40">
                          {r.slice(0, 6).map((c, j) => (
                            <td key={j} className="px-2 py-1.5 truncate max-w-[12rem]">{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Override */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground/70 font-medium mb-1.5">
                Not quite right? Pick manually
              </div>
              <RadioGroup
                value={chosenClass}
                onValueChange={(v) => setOverride(v)}
                className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                data-testid="import-hub-class-override"
              >
                {['statements', 'investments', 'accounts', 'recurring'].map((c) => (
                  <label
                    key={c}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer text-xs font-medium transition-colors ${
                      chosenClass === c ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'
                    }`}
                    data-testid={`import-hub-class-option-${c}`}
                  >
                    <RadioGroupItem value={c} className="sr-only" />
                    {chosenClass === c && <Check className="h-3 w-3" />}
                    <span>{CLASS_LABELS[c]?.split(' ')[0] || c}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/40">
              <Button variant="ghost" onClick={reset} className="rounded-lg" data-testid="import-hub-reset">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Pick another file
              </Button>
              {isSupported ? (
                <Button onClick={proceed} className="rounded-lg" data-testid="import-hub-continue">
                  Continue <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              ) : (
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">
                    Bulk-{chosenClass} import is coming in the next release.
                  </div>
                  <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                    For now, add these manually from the {chosenClass === 'accounts' ? 'Accounts' : 'Recurring'} page.
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick jumps for users who know what they want */}
      {!file && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate('/import/statements')}
            className="rounded-2xl border border-border/60 bg-card hover:bg-accent/30 transition-colors p-4 text-left"
            data-testid="import-hub-quickjump-statements"
          >
            <div className="text-sm font-semibold">Statements</div>
            <div className="text-xs text-muted-foreground mt-1">PDF / CSV / OFX / XLSX from your bank or credit card.</div>
          </button>
          <button
            type="button"
            onClick={() => setActiveProcessor('investments')}
            className="rounded-2xl border border-border/60 bg-card hover:bg-accent/30 transition-colors p-4 text-left"
            data-testid="import-hub-quickjump-investments"
          >
            <div className="text-sm font-semibold">Investments</div>
            <div className="text-xs text-muted-foreground mt-1">MFs, stocks, ETFs from CAMS, Kfintech, brokers.</div>
          </button>
        </div>
      )}

      {/* Phase 84.18 — Uploaded Statements panel. Always visible so users
          can resume held imports and audit their upload history even
          after the source PDFs have been auto-purged (~24h retention). */}
      {!file && <UnlinkedTransactionsCard />}
      {!file && <UploadedStatementsPanel />}
    </div>
  );
}
